import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Admin.css';

export default function AddEditProduct() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { api, BASE_URL } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'fruits',
    price: '',
    unit: 'kg',
    stock: '',
    rating: '4.5',
    is_active: true,
    discount_percent: '0'   // ✅ NEW
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isEdit) {
      fetchProduct();
    }
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      const product = response.data;

      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || 'fruits',
        price: product.price || '',
        unit: product.unit || 'kg',
        stock: product.stock || '',
        rating: product.rating || '4.5',
        is_active: product.is_active !== undefined ? Boolean(product.is_active) : true,
        discount_percent: product.discount_percent ?? '0'   // ✅ NEW
      });

      if (product.image_url) {
        setExistingImage(product.image_url);
      }
    } catch (err) {
      console.error('Fetch product error:', err);
      setError('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    setError('');
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const validate = () => {
    if (!formData.name.trim()) return 'Product name is required';
    if (!formData.category) return 'Category is required';
    if (!formData.price || isNaN(formData.price)) return 'Valid price is required';
    if (!formData.unit) return 'Unit is required';
    if (!formData.stock || isNaN(formData.stock)) return 'Valid stock is required';
    const disc = Number(formData.discount_percent);
    if (isNaN(disc) || disc < 0 || disc > 100) return 'Discount must be between 0 and 100';
    return null;
  };

  // ✅ Helper: calculate discounted price
  const getDiscountedPrice = () => {
    const price = parseFloat(formData.price);
    const disc = parseFloat(formData.discount_percent);
    if (!price || isNaN(price) || !disc || isNaN(disc) || disc <= 0) return null;
    return (price - (price * disc) / 100).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMsg = validate();
    if (errorMsg) {
      setError(errorMsg);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const formDataToSend = new FormData();

      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('description', formData.description.trim());
      formDataToSend.append('category', formData.category);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('unit', formData.unit);
      formDataToSend.append('stock', formData.stock);
      formDataToSend.append('rating', formData.rating);
      formDataToSend.append('is_active', formData.is_active ? '1' : '0');
      formDataToSend.append('discount_percent', formData.discount_percent || '0');  // ✅ NEW

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      } else if (existingImage) {
        formDataToSend.append('image_url', existingImage);
      }

      if (isEdit) {
        await api.post(`/products/${id}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('Product updated successfully!');
      } else {
        await api.post('/products', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        alert('Product added successfully!');
      }

      navigate('/admin/products');
    } catch (err) {
      console.error('Save product error:', err);
      setError(err.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${BASE_URL}${cleanPath}`;
  };

  if (loading) {
    return <LoadingSpinner message="Loading product..." />;
  }

  const displayImage = imagePreview || (existingImage ? getImageUrl(existingImage) : null);
  const discountedPrice = getDiscountedPrice();

  return (
    <div className="admin-add-edit-product">
      <div className="container">
        <h1 className="page-title">
          <span className="title-icon">{isEdit ? '✏️' : '➕'}</span>
          {isEdit ? 'Edit Product' : 'Add Product'}
        </h1>

        <form onSubmit={handleSubmit} className="product-form">
          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span className="alert-text">{error}</span>
            </div>
          )}

          {/* Image Upload */}
          <div className="card">
            <h3 className="card-title">Product Image</h3>

            <div className="image-upload-section">
              <div className="image-preview-container">
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt="Preview"
                    className="image-preview"
                  />
                ) : (
                  <div className="image-placeholder">
                    <span className="placeholder-icon">🖼️</span>
                    <span className="placeholder-text">No image selected</span>
                  </div>
                )}
              </div>

              <div className="image-upload-controls">
                <label htmlFor="image" className="btn btn-outline">
                  📷 Choose Image
                </label>
                <input
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                  disabled={saving}
                />
                {(imagePreview || existingImage) && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setImageFile(null);
                      setImagePreview(null);
                      setExistingImage(null);
                    }}
                  >
                    ✕ Remove Image
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="card">
            <h3 className="card-title">Basic Information</h3>

            <div className="form-group">
              <label htmlFor="name">Product Name *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g. Fresh Tomatoes"
                disabled={saving}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Product description..."
                rows="4"
                disabled={saving}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  disabled={saving}
                  required
                >
                  <option value="fruits">🍎 Fruits</option>
                  <option value="vegetables">🥬 Vegetables</option>
                  <option value="meat">🥩 Meat</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="unit">Unit *</label>
                <select
                  id="unit"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  disabled={saving}
                  required
                >
                  <option value="">-- Select Unit --</option>
                  <option disabled>── Weight ──</option>
                  <option value="kg">Kilogram (kg)</option>
                  <option value="500g">Half Kg (500g)</option>
                  <option value="250g">Quarter Kg (250g)</option>
                  <option value="gram">Gram (g)</option>
                  <option value="100g">100 Gram</option>
                  <option disabled>── Count ──</option>
                  <option value="piece">Piece</option>
                  <option value="dozen">Dozen (12)</option>
                  <option value="half_dozen">Half Dozen (6)</option>
                  <option value="quarter_dozen">Quarter Dozen (3)</option>
                  <option value="pack">Pack</option>
                  <option value="bundle">Bundle</option>
                  <option value="tray">Tray</option>
                  <option value="box">Box</option>
                  <option value="bag">Bag</option>
                  <option value="crate">Crate</option>
                  <option disabled>── Volume ──</option>
                  <option value="liter">Liter (L)</option>
                  <option value="500ml">Half Liter (500ml)</option>
                  <option value="250ml">250 ml</option>
                </select>
              </div>
            </div>
          </div>

          {/* Pricing & Stock */}
          <div className="card">
            <h3 className="card-title">Pricing & Stock</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="price">Price (Rs) *</label>
                <input
                  id="price"
                  name="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  placeholder="0.00"
                  disabled={saving}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="stock">Stock Quantity *</label>
                <input
                  id="stock"
                  name="stock"
                  type="number"
                  value={formData.stock}
                  onChange={handleChange}
                  placeholder="0"
                  disabled={saving}
                  required
                />
              </div>
            </div>

            {/* ✅ NEW: Discount Section */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="discount_percent">Discount (%)</label>
                <input
                  id="discount_percent"
                  name="discount_percent"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.discount_percent}
                  onChange={handleChange}
                  placeholder="0"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label>Discounted Price</label>
                <div style={{
                  padding: '10px 14px',
                  background: discountedPrice ? '#f0fff4' : '#f9f9f9',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontWeight: '600',
                  color: discountedPrice ? '#2d7a45' : '#999',
                  fontSize: '1rem',
                  minHeight: '42px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {discountedPrice
                    ? `Rs ${discountedPrice} (Save ${formData.discount_percent}%)`
                    : 'No discount applied'}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="rating">Rating</label>
                <input
                  id="rating"
                  name="rating"
                  type="number"
                  step="0.1"
                  min="0"
                  max="5"
                  value={formData.rating}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  <span>Product is Active</span>
                </label>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="form-actions">
            <button
              type="submit"
              className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-small"></span>
                  Saving...
                </>
              ) : (
                isEdit ? 'Update Product' : 'Add Product'
              )}
            </button>

            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/admin/products')}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}